// The window the document dialogs live in (FS-3, owner 2026-10-19).
//
// ── Why this file exists, and why it is NOT the workspace's Dialog ──────────────────────────────
// Four modals in this folder and in ClientPortal/index.tsx each carried their OWN copy of the same
// fifteen-line overlay: `position: fixed; inset: 0`, a 980 card, and a backdrop `onMouseDown` that
// closed it. Four copies meant four chances for an exit to behave differently from its neighbour,
// and they already did: not one of them had an X, an Esc key, or any check that the operator had
// typed something. This folds the four into one.
//
// It is deliberately NOT CompanyWorkspace/dialog.tsx's `Dialog`, and that is a measured decision,
// not a preference. These modals are rendered by the CLIENT PORTAL (`/portal/*`, a merchant's own
// login) as well as by the staff workspace, and the portal's window must not change under the
// customer:
//   · `Dialog` is centred with its content scrolling inside a 90vh card. The portal's window is
//     TOP-aligned at `5vh` with the whole overlay scrolling — a 980-wide form that is taller than
//     the viewport reads top-down, and switching it to an inner scroller moves every control.
//   · `Dialog` paints with the staff theme tokens (`var(--bg-surface)`, `var(--text-primary)`,
//     `var(--shadow-md)`). The portal paints from `shared.ts`'s own light palette (`card` = #fff /
//     #e3e7ea). Under the staff dark theme those tokens flip and the portal's hard-coded children
//     would not.
//   · zIndex 10002 is hard-coded in `Dialog`. In the portal that is the wrong layer — see `layer`.
// So: one shell for these four, with the SAME contract and the same prop names as the workspace's
// (`fullscreen`, `dirty`, `busy`, `docNo`), so a reader of either file recognises the other. What is
// shared is the behaviour; what differs is the paint, and that difference is the whole reason.
//
// The body-scroll lock and the Esc stack are NOT copied here: they are the workspace's, in utils/bodyLock.ts and
// utils/escLayer.ts, and this window takes the SAME ones (batch 12 tail T2, TL-10). A second copy of either would be the
// fork this file exists to avoid — and two Esc stacks are exactly what let one Esc close both a Sales window and the
// entries panel opened over it. The lock (and a pinned `footer`) apply ONLY when `fullscreen`, the staff workspace's
// window: the portal never passes it, so its windows keep their DOM, their inline styles and their Back to the byte.
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { btnGhost, card } from './shared';
import { useUnsavedGuard } from '../../utils/unsaved';
import { EscDepthCtx, useEscLayer } from '../../utils/escLayer';
import { lockBodyScroll } from '../../utils/bodyLock';

// ── Which stacking layer, and why not simply "higher" ───────────────────────────────────────────
// The plan said to raise these modals above the shared shell's 10002. Measured first, because the
// number is not free: raise it blindly and the window lands on top of things the house deliberately
// keeps above a dialog. What actually sits where (grep of `zIndex:` across src/):
//
//   50   these four modals, and the portal's other document modals
//   60   the portal's SendDocModal / ConvertQuotationModal, and PickerSelect's dropdown
//   100  the workspace's ➕ Fab        300  MailApp's chrome
//   2000 Toast · 4500 NewMailToast · 4600 PopupHomeNotice
//   10001 the workspace Drawer · 10002 the workspace Dialog
//   10003–10005 the composers a dialog opens FROM a dialog (Peak, Workflows, Tax)
//   10080 MyTasks · 10130 Jobs · 10150 DropFolderPicker · 10200/10300 Chat panels
//   11000/11002 Contact, PettyCash · 11500 PenaltyCard · 12000 AnnualTax
//   13000 SlipAlert, ChatApp · 13100 DocNoLink's hover card · 20000 ScrollHint
//
// So there are two right answers, one per host, and the layer belongs to the HOST rather than to
// the window's size:
//   · PORTAL (default, 50): unchanged, to the number. The portal's own stack is self-consistent —
//     60 is where its sibling modals sit so one can be opened over another, and 200 is its row
//     action menu. Raising the portal's document window to 10002 would put it above the portal's
//     own menus for no gain and change a live customer screen.
//   · WORKSPACE (10002): the SAME layer as the shared shell, not above it. At 50 a staff
//     full-screen window would render UNDER the workspace's Fab (100), MailApp's chrome (300) and
//     the toasts at 2000–4600 — a window that fills the screen with a mail toast floating over it.
//     10002 is where the house already puts a workspace dialog, so this one covers exactly what
//     those cover and still sits UNDER everything from 10003 up: the alert modals, the Chat panel,
//     DocNoLink's hover card and ScrollHint. Choosing 10003+ would start covering those, and
//     ScrollHint (pointer-events: none) and DocNoLink are meant to be on top of a dialog.
export const PORTAL_LAYER = 50;
export const WORKSPACE_LAYER = 10002;

// Esc belongs to the TOP-most window only — the deepest on the ONE stack both shells share (utils/escLayer.ts), a tie
// going to the later registration. A Dialog opened inside one of these windows (the staff Sales window's "Reverse JV-…"
// entries panel) is one level deeper and answers alone; two windows side by side (the portal's) keep last-opened-wins.

/** The ONE place a document window closes. Every exit — the X, Esc, the backdrop and the Cancel the
 *  caller renders — goes through here, so closing never saves and never discards silently, and asks
 *  ONCE. The house precedent is Admin/JobRequiredDocs.tsx: a `dirty` flag on the form, guarded with
 *  `window.confirm`. Same idiom, lifted into the shell so the four exits cannot drift apart.
 *  Nothing happens while a request is in flight — a close mid-save would leave the operator unable
 *  to tell whether it saved. */
export function useCloseGate(onClose: () => void, busy?: boolean, dirty?: boolean): () => void {
  const { t } = useTranslation();
  return useCallback(() => {
    if (busy) return;
    if (dirty && !window.confirm(t('p_dlg_discard'))) return;
    onClose();
  }, [busy, dirty, onClose, t]);
}

/** Has the operator got anything to lose? The form as it opened, against the form now.
 *
 *  ⛔ DERIVED, not a flag the form sets. JobRequiredDocs' `dirty: true` on every patch works because
 *  that form has one patch function; CreateDocModal has thirty-odd inputs and three setters that
 *  fire from effects, and a `touch()` missing from ONE of them is a silently discarded form — the
 *  exact failure this round exists to stop. A snapshot cannot be forgotten, and it also gets the
 *  nicer answer right: type a character, delete it again, and there is nothing to ask about.
 *
 *  `rebaseKey` is for the one thing that changes the form without the operator: a Remark's stored
 *  image URLs are swapped for object URLs after an async fetch (`useRemarkImages.seed`). Bump the
 *  key when that lands and the baseline moves with it, so the window does not open already dirty.
 *
 *  ⛔ The baseline is taken DURING RENDER, not in an effect, and that is the whole correctness of
 *  it. An effect commits AFTER the render it belongs to and does not schedule another, so a
 *  baseline moved from an effect leaves exactly one committed render holding the OLD base against
 *  the NEW snapshot — `dirty` true with nothing typed — and it stays that way until some unrelated
 *  re-render clears it. Measured: a receipt whose stored Remark holds a picture asked "Discard the
 *  unsaved changes?" on the first click of the X, having been open for one second and never typed
 *  in. Rebasing here answers in the same pass the snapshot changed, so the window is never briefly
 *  dirty on the system's behalf. */
export function useDirtySnapshot(snapshot: string, rebaseKey: number = 0): boolean {
  const base = useRef<string | null>(null);
  const basedOn = useRef<number | null>(null);
  if (basedOn.current !== rebaseKey) { basedOn.current = rebaseKey; base.current = snapshot; }
  return base.current !== null && snapshot !== base.current;
}

/** What a document modal needs, in one call: whether there is anything to lose, and the gated close
 *  for the Save/Cancel row it renders itself. The window gets `dirty` and gates its own three exits
 *  from the same value, so the four exits cannot answer differently. */
export function useDocWindowGate(snapshot: string, onClose: () => void, busy?: boolean, rebaseKey = 0): { dirty: boolean; close: () => void } {
  const dirty = useDirtySnapshot(snapshot, rebaseKey);
  const close = useCloseGate(onClose, busy, dirty);
  return { dirty, close };
}

/** The document window.
 *
 *  ONE new shape and TWO historical ones, because ⛔ the portal's windows must stay where they are:
 *    · default — the 980 card, top-aligned at 5vh, the whole OVERLAY scrolling. CreateDocModal,
 *      CreateNoteModal and (at width 560) EditReceiptModal have always looked like this.
 *    · `centred` — vertically centred, the PANEL scrolling inside 92vh. Only CreateReceiptModal,
 *      which has always looked like that; the flag exists so adopting this shell does not move it.
 *    · `fullscreen` — the FS-1 document window: fills the viewport, title and document number top
 *      left, a prominent X top right, content scrolling by itself, the caller's Save / Cancel row
 *      pinned below it (`footer`), and the page behind locked (batch 12 tail T2 — the workspace
 *      Dialog's parity). This is the shape the staff workspace opens.
 *  Everything new is opt-in: a caller that passes none of them gets exactly the window it had
 *  before, plus the X, the Esc key and the dirty check it never had. */
export function DocWindow({ title, titleSize = 15, capitalize, subtitle, docNo, onClose, busy, dirty, fullscreen, centred, layer = PORTAL_LAYER, width = 980, footer, children }: {
  title: React.ReactNode;
  /** Kept per call site so no portal heading changes size: 15 everywhere except CreateReceiptModal. */
  titleSize?: number;
  /** ⛔ Per call site, and off by default. ONLY CreateDocModal's heading has ever been capitalised,
   *  and it has to be: it interpolates a doc-kind word that is stored lower case ('invoice'), so
   *  "New {{docKind}}" renders as "New Invoice". Applied to the other three it would rewrite their
   *  headings — 'Record a payment' → 'Record A Payment' — which is a portal screen changing under
   *  the customer for nothing. No-op in Thai and Chinese, which is why it is easy to miss. */
  capitalize?: boolean;
  subtitle?: React.ReactNode;
  /** The document's number, beside the title — the same shape the workspace shell gives it. */
  docNo?: React.ReactNode;
  onClose: () => void;
  busy?: boolean;
  /** The caller has unsaved work, so every exit asks first. See useDirtySnapshot. */
  dirty?: boolean;
  /** Fill the viewport instead of the 980 card. ⛔ Staff only: the portal keeps its own window. */
  fullscreen?: boolean;
  /** CreateReceiptModal's historical shape: centred, the panel scrolling inside 92vh. */
  centred?: boolean;
  /** PORTAL_LAYER (the default, unchanged) or WORKSPACE_LAYER. See the note above. */
  layer?: number;
  width?: number;
  /** (Batch 12 tail T2) The caller's Save / Cancel row, PINNED below the scrolling content of the full-screen window,
   *  so Save stays reachable at the end of a long form. ⛔ Passed only with `fullscreen` (the staff hosts): a card has
   *  no pinned region, so its caller keeps the row inline at the end of `children`, exactly as before — the portal
   *  passes neither. (Given to a card anyway, it is drawn after the content, so a button is never lost.) */
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const close = useCloseGate(onClose, busy, dirty);
  // Back, an in-app jump, and the window being unmounted from above: the page-wide registry guards
  // them with the workspace's question (utils/unsaved.ts). ⛔ ON THE WORKSPACE LAYER ONLY — the layer
  // is the host's (see above), and the portal's windows must not change under the customer, so a
  // portal window registers nothing and its Back behaves exactly as it always has.
  useUnsavedGuard(layer === WORKSPACE_LAYER && !!dirty, layer === WORKSPACE_LAYER && !!busy);

  // Esc: registered ONCE, on mount, on the one stack both shells share (utils/escLayer.ts), at this window's nesting
  // depth, reading `close` through a ref — so the handler can never be holding a stale `dirty` and discard a form it
  // believes is empty. ⛔ NOT re-registered when `close` changes: re-registering would move this window to the TOP, and
  // the window BEHIND would steal Esc the moment its own `dirty` flipped — measured with two windows open and only the
  // back one turning dirty, Esc closed the back one.
  const depth = React.useContext(EscDepthCtx) + 1;
  useEscLayer(close, depth);

  // FS-10, the workspace Dialog's parity (batch 12 tail T2): the full-screen window covers the page, so the page behind
  // must not scroll under it and must be where it was when the window closes. ⛔ Only `fullscreen` — the staff window;
  // the portal's card leaves the customer's page exactly as it always has.
  useEffect(() => {
    if (!fullscreen) return;
    return lockBodyScroll();
  }, [fullscreen]);

  const panel: React.CSSProperties = fullscreen
    ? { position: 'absolute', inset: 0, width: '100%', maxWidth: 'none', background: '#fff', borderRadius: 0,
        padding: 20, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 10 }
    : centred
      ? { ...card, width, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto' }
      : { ...card, width, maxWidth: '96vw' };

  return (
    <EscDepthCtx.Provider value={depth}>
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: layer, display: 'flex',
        alignItems: fullscreen ? 'stretch' : centred ? 'center' : 'flex-start', justifyContent: 'center',
        overflow: fullscreen || centred ? 'hidden' : 'auto', padding: fullscreen || centred ? 0 : '5vh 12px',
      }}
        onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
        <div style={panel} role="dialog" aria-modal="true">
          {/* The title row the four modals used to render themselves, now with the X they never had. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6, flex: 'none' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: fullscreen ? 17 : titleSize, fontWeight: 800, ...(capitalize ? { textTransform: 'capitalize' as const } : {}) }}>{title}</div>
              {docNo ? <div style={{ fontSize: 12.5, fontWeight: 700, color: '#7c8894', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{docNo}</div> : null}
              {subtitle ? <div style={{ fontSize: 12.5, color: '#7c8894', marginTop: 2 }}>{subtitle}</div> : null}
            </div>
            <button type="button" onClick={close} disabled={busy} aria-label={t('p_dlg_close')} title={t('p_dlg_close')}
              style={{ ...btnGhost, flex: 'none', padding: fullscreen ? '6px 11px' : '5px 9px', fontSize: fullscreen ? 16 : 13, opacity: busy ? 0.5 : 1 }}>
              <i className="ti ti-x" aria-hidden />
            </button>
          </div>
          {/* minHeight 0 is what actually lets a flex child scroll instead of growing the panel. Only
              the full-screen window scrolls internally; the card lets the overlay scroll, as it did. */}
          {fullscreen
            ? <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>{children}</div>
            : children}
          {/* The pinned Save / Cancel row of the full-screen window, outside the scroller (the workspace Dialog's shape). */}
          {fullscreen && footer ? <div style={{ flex: 'none', paddingTop: 10, borderTop: '1px solid #e3e7ea' }}>{footer}</div> : null}
          {!fullscreen && footer ? footer : null}
        </div>
      </div>
    </EscDepthCtx.Provider>
  );
}
